import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { Modal } from "@mui/material";
import { httpsCallable } from "firebase/functions";
import React, { useEffect, useState } from "react";
import { functions } from "../../firebase";
import styles from "./rsvp.module.css";

// The guest list is admin-only in Firestore, so the RSVP modal reads and writes
// through Cloud Functions scoped to the entered invite code.
const getRsvpParty = httpsCallable<{ inviteCode: string }, { guests: Person[] }>(functions, "getRsvpParty");
const submitRsvpCallable = httpsCallable(functions, "submitRsvp");
const updateRsvpGuestName = httpsCallable(functions, "updateRsvpGuestName");
const addRsvpGuest = httpsCallable(functions, "addRsvpGuest");

const callableErrorMessage = (error: unknown, fallback: string): string => {
  const code = (error as { code?: string })?.code;
  const message = (error as { message?: string })?.message;
  return code === "functions/resource-exhausted" || code === "functions/failed-precondition" || code === "functions/invalid-argument"
    ? message || fallback
    : fallback;
};

interface Person {
  Email: string;
  id: string;
  "First Name": string;
  "Last Name": string;
  Party: string;
  "Wedding Day - RSVP": string;
  "BBQ Day - RSVP"?: string;
  inviteCode: string;
  inviteSent?: string;
  Diet?: string;
  Phone?: string;
  "Street Address 1"?: string;
  "Street Address 2"?: string;
  City?: string;
  "State/Province"?: string;
  "Zip/Postal Code"?: string;
  Country?: string;
  allowPlusOne?: boolean;
  plusOneUsed?: boolean;
  plusOneGuestId?: string;
}

interface RsvpState {
  [personId: string]: "yes" | "no" | "no-response";
}

interface BbqRsvpState {
  [personId: string]: "yes" | "no" | "no-response";
}

interface RSVPComponentProps {
  className?: string;
  showInline?: boolean;
  linkStyle?: string;
  prefilledCode?: string;
  onClose?: () => void;
  isAdminMode?: boolean;
}

const RSVPComponent: React.FC<RSVPComponentProps> = ({
  className,
  showInline = false,
  linkStyle,
  prefilledCode,
  onClose,
  isAdminMode = false,
}) => {
  const [inviteCode, setInviteCode] = useState<string>("");
  const [partyEmail, setPartyEmail] = useState<string>("");
  const [people, setPeople] = useState<Person[]>([]);
  const [rsvps, setRsvps] = useState<RsvpState>({});
  const [bbqRsvps, setBbqRsvps] = useState<BbqRsvpState>({});
  const [dietaryRestrictions, setDietaryRestrictions] = useState<{ [personId: string]: string }>({});
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [showAddGuest, setShowAddGuest] = useState(false);
  const [newGuestFirstName, setNewGuestFirstName] = useState('');
  const [newGuestLastName, setNewGuestLastName] = useState('');
  const [editingGuestId, setEditingGuestId] = useState<string | null>(null);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [plusOneAvailable, setPlusOneAvailable] = useState(false);
  const [plusOneUsed, setPlusOneUsed] = useState(false);

  useEffect(() => {
    if (prefilledCode) {
      setInviteCode(prefilledCode);
      handleOpen();
      // small delay ensures modal is mounted
      setTimeout(() => {
        fetchPeople(prefilledCode);
      }, 100);
    }
  }, [prefilledCode]);

  const handleInviteCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInviteCode(event.target.value);
  };

  const fetchPeople = async (codeOverride?: string) => {
    const codeToUse = codeOverride || inviteCode;
    if (!codeToUse) return;

    let users: Person[] = [];
    try {
      const result = await getRsvpParty({ inviteCode: codeToUse });
      users = result.data.guests;
    } catch (error) {
      if ((error as { code?: string })?.code !== "functions/not-found") {
        setError(callableErrorMessage(error, "Oops! Something went wrong looking up your invite. Please try again."));
        return;
      }
    }

    if (users.length > 0) {
      setPeople(users);

      const initialRsvps: RsvpState = users.reduce<RsvpState>((acc, person) => {
        const rsvp = person["Wedding Day - RSVP"];
        if (rsvp === "Yes") {
          acc[person.id] = "yes";
        } else if (rsvp === "No") {
          acc[person.id] = "no";
        } else {
          acc[person.id] = "no-response";
        }
        return acc;
      }, {});
      setRsvps(initialRsvps);

      const initialBbqRsvps: BbqRsvpState = users.reduce<BbqRsvpState>((acc, person) => {
        const bbqRsvp = person["BBQ Day - RSVP"];
        if (bbqRsvp === "Yes") {
          acc[person.id] = "yes";
        } else if (bbqRsvp === "No") {
          acc[person.id] = "no";
        } else {
          acc[person.id] = "no"; // Default to "no" for BBQ as requested
        }
        return acc;
      }, {});
      setBbqRsvps(initialBbqRsvps);
      setPartyEmail(users[0].Email || "");

      // Check if plus one is available and if it's been used
      setPlusOneAvailable(users.some(u => u.allowPlusOne === true));
      setPlusOneUsed(users.some(u => u.plusOneUsed === true));

      setError('');
    } else {
      setError("Sorry we didn't find you on the list, try again or reach out and we can assist you");
    }
  };

  const handleRadioButtonChange = (personId: string, value: "yes" | "no" | "no-response") => {
    setRsvps((prevRsvps) => ({
      ...prevRsvps,
      [personId]: value,
    }));
  };

  const handleBbqRadioButtonChange = (personId: string, value: "yes" | "no") => {
    setBbqRsvps((prevBbqRsvps) => ({
      ...prevBbqRsvps,
      [personId]: value,
    }));
  };

  const handleDietaryChange = (personId: string, value: string) => {
    setDietaryRestrictions((prevDiet) => ({
      ...prevDiet,
      [personId]: value,
    }));
  };

  const handleEmailChange = (value: string) => {
    setPartyEmail(value);
    setPeople((prevPeople) =>
      prevPeople.map((person) => ({
        ...person,
        Email: value,
      }))
    );
  };

  const startEditingGuest = (person: Person) => {
    setEditingGuestId(person.id);
    setEditFirstName(person["First Name"]);
    setEditLastName(person["Last Name"]);
  };

  const cancelEditingGuest = () => {
    setEditingGuestId(null);
    setEditFirstName('');
    setEditLastName('');
    setError('');
  };

  const updateGuestName = async (guestId: string) => {
    if (!editFirstName.trim() || !editLastName.trim()) {
      setError("Please enter both first and last name.");
      return;
    }

    try {
      await updateRsvpGuestName({
        inviteCode: people[0]?.inviteCode || inviteCode,
        guestId,
        firstName: editFirstName.trim(),
        lastName: editLastName.trim(),
      });

      // Reset edit state
      setEditingGuestId(null);
      setEditFirstName('');
      setEditLastName('');
      setError('');

      // Refresh the guest list
      await fetchPeople();
    } catch (error) {
      console.error("Error updating guest name:", error);
      setError(callableErrorMessage(error, "Failed to update guest name. Please try again."));
    }
  };

  const addGuestToParty = async () => {
    if (!newGuestFirstName.trim() || !newGuestLastName.trim()) {
      setError("Please enter both first and last name for the new guest.");
      return;
    }

    if (people.length === 0) {
      setError("No party found to add guest to.");
      return;
    }

    // Validate plus one hasn't been used (only for non-admin mode)
    if (!isAdminMode && plusOneUsed) {
      setError("You have already added your plus one.");
      return;
    }

    try {
      await addRsvpGuest({
        inviteCode: people[0].inviteCode,
        firstName: newGuestFirstName.trim(),
        lastName: newGuestLastName.trim(),
        email: partyEmail,
      });

      if (!isAdminMode && plusOneAvailable) {
        setPlusOneUsed(true);
      }

      // Reset the form
      setNewGuestFirstName('');
      setNewGuestLastName('');
      setShowAddGuest(false);
      setError('');

      // Refresh the guest list
      await fetchPeople();
    } catch (error) {
      console.error("Error adding guest:", error);
      setError(callableErrorMessage(error, "Failed to add guest. Please try again."));
    }
  };

  const submitRsvp = async () => {
    try {
      await submitRsvpCallable({
        inviteCode: people[0].inviteCode,
        email: partyEmail,
        responses: people.map((person) => ({
          id: person.id,
          rsvp: rsvps[person.id],
          bbq: bbqRsvps[person.id] === "yes" ? "yes" : "no",
          diet: dietaryRestrictions[person.id] || undefined,
        })),
      });

      setSubmitted(true);
    } catch (error) {
      console.log(error);
      setError(callableErrorMessage(error, "Oops! Something went wrong while submitting your RSVP. Please try again."));
    }
  };

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
    setPeople([]);
    setRsvps({});
    setBbqRsvps({});
    setSubmitted(false);
    setError('');
    setShowAddGuest(false);
    setNewGuestFirstName('');
    setNewGuestLastName('');
    setEditingGuestId(null);
    setEditFirstName('');
    setEditLastName('');
    setPlusOneAvailable(false);
    setPlusOneUsed(false);
    if (onClose) onClose(); // ✅ trigger parent callback
  };

  const renderInviteCode = () => (
    <div className={styles.content}>
      <p>We are so excited to invite you to celebrate this special day with us.</p>
      <p>Please confirm your attendance by entering your unique invite code found on your physical invite and let us know if you can attend our wedding.</p>
      <input
        className={styles.textInput}
        type="text"
        placeholder="Enter Invite Code"
        value={inviteCode}
        onChange={handleInviteCodeChange}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            fetchPeople();  // Trigger the same action as button click
          }
        }}
      />
      <button className={`primary-btn ${styles.button}`} onClick={() => fetchPeople()}>
        Submit
      </button>
      {error && <div className={styles.error}>{error}</div>}
      <p className={styles.note}>If you have not received your physical invite or have misplaced it please reach out to us.</p>
    </div>
  );

  const renderParty = () => {
    const contactVerb = people.length === 1 ? 'you' : 'your party';

    return (
      <div className={styles.content}>
        {people.length === 1 ? (
          <>
            <p>We are delighted to have you join us in celebrating this special occasion.</p>
            <p>Please take a moment to confirm your attendance.</p>
          </>
        ) : (
          <>
            <p>We are delighted to have you and your loved ones join us in celebrating this special occasion.</p>
            <p>Below is a list of the guests invited in your party. Kindly take a moment to confirm each guest's attendance.</p>
          </>
        )}
        {people.map((person) => (
          <div className={styles.response} key={person.id}>
            {editingGuestId === person.id ? (
              <>
                <div className={styles.name}>Edit Guest Name</div>
                <div className={styles.question}>First Name</div>
                <input
                  className={styles.textInput}
                  type="text"
                  placeholder="First Name"
                  value={editFirstName}
                  onChange={(e) => setEditFirstName(e.target.value)}
                />
                <div className={styles.question}>Last Name</div>
                <input
                  className={styles.textInput}
                  type="text"
                  placeholder="Last Name"
                  value={editLastName}
                  onChange={(e) => setEditLastName(e.target.value)}
                />
                <div style={{ display: 'flex', gap: '10px', marginTop: '10px', marginBottom: '10px' }}>
                  <button className="primary-btn" onClick={() => updateGuestName(person.id)}>
                    Save Changes
                  </button>
                  <button className="accent-btn" onClick={cancelEditingGuest}>
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div className={styles.name}>{person["First Name"]} {person["Last Name"]}</div>
                <EditIcon
                  onClick={() => startEditingGuest(person)}
                  style={{ cursor: 'pointer', fontSize: '20px', color: '#666' }}
                />
              </div>
            )}
            <div className={styles.question}>Are you able to attend the wedding and reception?</div>
            <div className={styles.options}>
              <label>
                <input
                  type="radio"
                  name={`rsvp-${person.id}`}
                  checked={rsvps[person.id] === "yes"}
                  onChange={() => handleRadioButtonChange(person.id, "yes")}
                />
                Yes
              </label>
              <label>
                <input
                  type="radio"
                  name={`rsvp-${person.id}`}
                  checked={rsvps[person.id] === "no"}
                  onChange={() => handleRadioButtonChange(person.id, "no")}
                />
                No
              </label>
              {isAdminMode && (
                <label>
                  <input
                    type="radio"
                    name={`rsvp-${person.id}`}
                    checked={rsvps[person.id] === "no-response"}
                    onChange={() => handleRadioButtonChange(person.id, "no-response")}
                  />
                  No Response
                </label>
              )}
            </div>
            <div className={styles.question}>Are you able to attend the kick on BBQ the day after?</div>
            <div className={styles.options}>
              <label>
                <input
                  type="radio"
                  name={`bbq-rsvp-${person.id}`}
                  checked={bbqRsvps[person.id] === "yes"}
                  onChange={() => handleBbqRadioButtonChange(person.id, "yes")}
                />
                Yes
              </label>
              <label>
                <input
                  type="radio"
                  name={`bbq-rsvp-${person.id}`}
                  checked={bbqRsvps[person.id] === "no"}
                  onChange={() => handleBbqRadioButtonChange(person.id, "no")}
                />
                No
              </label>
            </div>
            <div className={styles.question}>Do you have dietary restrictions?</div>
            <div className={styles.options}>
              <input
                className={styles.textInput}
                placeholder="Enter dietary restrictions"
                value={dietaryRestrictions[person.id] || ""}
                onChange={(e) => handleDietaryChange(person.id, e.target.value)}
              />
            </div>
          </div>
        ))}

        {(() => {
          // Determine if add guest button should be shown
          const canAddPlusOne = !isAdminMode && plusOneAvailable && !plusOneUsed;
          const showAddGuestButton = isAdminMode || canAddPlusOne;

          if (!showAddGuestButton) return null;

          if (showAddGuest) {
            return (
              <div className={styles.response}>
                <div className={styles.name}>New Guest</div>
                <div className={styles.question}>First Name</div>
                <input
                  className={styles.textInput}
                  type="text"
                  placeholder="First Name"
                  value={newGuestFirstName}
                  onChange={(e) => setNewGuestFirstName(e.target.value)}
                />
                <div className={styles.question}>Last Name</div>
                <input
                  className={styles.textInput}
                  type="text"
                  placeholder="Last Name"
                  value={newGuestLastName}
                  onChange={(e) => setNewGuestLastName(e.target.value)}
                />
                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                  <button className="primary-btn" onClick={addGuestToParty}>
                    Save Guest
                  </button>
                  <button
                    className="accent-btn"
                    onClick={() => {
                      setShowAddGuest(false);
                      setNewGuestFirstName('');
                      setNewGuestLastName('');
                      setError('');
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            );
          } else {
            return (
              <button
                className="accent-btn"
                onClick={() => setShowAddGuest(true)}
                style={{ marginBottom: '20px' }}
              >
                {isAdminMode ? "+ Add Guest to Party" : "+ Add Your Plus One"}
              </button>
            );
          }
        })()}

        <div className={styles.question}>What is the best email to contact {contactVerb}?</div>
        <input
          className={styles.textInput}
          placeholder="Email address"
          value={partyEmail}
          onChange={(e) => handleEmailChange(e.target.value)}
        />
        <button className={`primary-btn ${styles.button}`} onClick={submitRsvp}>
          Submit RSVP
        </button>
        {error && <div className={styles.error}>{error}</div>}
      </div>
    );
  };

  const renderConfirmation = () => (
    <div className={styles.content}>
      <p>
        {Object.values(rsvps).includes("yes")
          ? "We truly appreciate your response, and we look forward to celebrating this special day with you!"
          : "Thank you for your response, though we'll miss you, we completely understand and wish you all the best."
        }
      </p>
      <p>If you need to update your responses you can do so at any time.</p>
    </div>
  );

  return (
    <div className={className}>
      {!showInline ? (
        <button className="primary-btn large-btn" onClick={handleOpen}>RSVP</button>
      ) : (
        <div className={linkStyle} onClick={handleOpen}>RSVP</div>
      )}
      <Modal open={open} onClose={handleClose}>
        <div className="modal-content">
          <CloseIcon className={styles.close} onClick={handleClose} />
          <div className={styles.title}>RSVP</div>
          {(!people.length && !submitted) && renderInviteCode()}
          {(people.length > 0 && !submitted) && renderParty()}
          {submitted && renderConfirmation()}
        </div>
      </Modal>
    </div>
  );
};

export default RSVPComponent;
